import { Type } from 'class-transformer';

import { ApiProperty, PickType } from '../../codegen';
import { PICK_RETURN_TIME_FIELDS } from '../../constants';
import { ROUTE_COLOR_VALUES, type TRouteColor } from '../../auth';
import {
  IsNumber,
  IsOptional,
  IsUUID,
  Min,
  IsArray,
  IsString,
  IsDate,
  IsIn,
  IsEnum,
} from '../../validators';

import { LobbyEntity, MatchEntity } from './matchmaking.entity';

export const MATCHMAKING_WS_NAMESPACE = 'matchmaking';
export const RESOLVE_ROUTE_REASON_VALUES = [
  'active-match',
  'draining-match',
  'default',
] as const;
export type TResolveRouteReason = (typeof RESOLVE_ROUTE_REASON_VALUES)[number];

// ========== RESPONSE DTOs ==========

export class ResolveRouteResponseDto {
  @ApiProperty()
  @IsOptional()
  @IsIn(ROUTE_COLOR_VALUES as unknown as string[])
  routeColor: TRouteColor | null;

  @ApiProperty()
  @IsIn(RESOLVE_ROUTE_REASON_VALUES as unknown as string[])
  reason: TResolveRouteReason;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsUUID()
  activeLobbyId?: string;
}

export class MatchReturnDto extends PickType(MatchEntity, [
  'id',
  'status',
  'gameId',
  'serverId',
  'serverAddress',
  'serverPort',
  'startedAt',
  'endedAt',
  'results',
  'winningTeam',
  'winnerId',
  'stake',
  'prize',
  'playerIds',
  'createdAt',
  'updatedAt',
] as const) {
  @ApiProperty()
  @IsOptional()
  @IsArray()
  players?: Array<{
    id: string;
    name: string;
    avatar?: string;
    rating: number;
  }>;
}

// ========== MATCH READ ALL DTO ==========

export const MATCH_CURSOR_FIELDS = ['createdAt'] as const;
export type TMatchCursorField = (typeof MATCH_CURSOR_FIELDS)[number];

export class MatchReadAllDto {
  // Polling mode - get records updated after this timestamp
  @ApiProperty()
  @IsOptional()
  @IsDate()
  @Type(() => Date)
  updatedAt?: Date;

  // Pagination cursor value (last seen value of cursorField)
  @ApiProperty()
  @IsOptional()
  @IsString()
  cursor?: string;

  // Cursor field for pagination (default: 'createdAt')
  @ApiProperty()
  @IsOptional()
  @IsIn(MATCH_CURSOR_FIELDS as unknown as string[])
  cursorField?: TMatchCursorField;

  // Window size (default: 100)
  @ApiProperty()
  @IsOptional()
  @IsNumber()
  @Min(1)
  @Type(() => Number)
  limit?: number;
}

// ========== MATCHMAKING DTOs ==========

// Request DTOs for the new system
export class FindGameDto {
  @ApiProperty()
  @IsNumber()
  gameId: number;

  @ApiProperty()
  @IsNumber()
  @Min(500)
  stake: number;
}

export enum EFreePlayDifficulty {
  easy = 'easy',
  medium = 'medium',
  hard = 'hard',
}

export class FreePlayDto {
  @ApiProperty()
  @IsNumber()
  gameId: number;

  @ApiProperty()
  @IsEnum(EFreePlayDifficulty)
  difficulty: EFreePlayDifficulty;
}

export class AcceptStakeOfferDto {
  @ApiProperty()
  @IsUUID()
  lobbyId: string;

  @ApiProperty()
  @IsNumber()
  @Min(500)
  acceptedStake: number;
}

export class DeclineStakeOfferDto {
  @ApiProperty()
  @IsUUID()
  lobbyId: string;
}

export class ConfirmGameDto {
  @ApiProperty()
  @IsUUID()
  lobbyId: string;
}

export class DeclineGameDto {
  @ApiProperty()
  @IsUUID()
  lobbyId: string;
}

export class ClientReadyDto {
  @ApiProperty()
  @IsUUID()
  lobbyId: string;
}

export enum ERematchRequestStatus {
  pending = 'pending',
  accepted = 'accepted',
  declined = 'declined',
  expired = 'expired',
}

export class RequestRematchDto {
  @ApiProperty()
  @IsUUID()
  sourceLobbyId: string;
}

export class AcceptRematchDto {
  @ApiProperty()
  @IsUUID()
  rematchRequestId: string;
}

export class DeclineRematchDto {
  @ApiProperty()
  @IsUUID()
  rematchRequestId: string;
}

export class RematchRequestResponseDto {
  @ApiProperty()
  @IsUUID()
  rematchRequestId: string;

  @ApiProperty()
  @IsUUID()
  sourceLobbyId: string;

  @ApiProperty()
  @IsNumber()
  gameId: number;

  @ApiProperty()
  @IsString()
  gameName: string;

  @ApiProperty()
  @IsNumber()
  @Min(0)
  stake: number;

  @ApiProperty()
  @IsNumber()
  initiatorId: number;

  @ApiProperty()
  @IsNumber()
  opponentId: number;

  @ApiProperty()
  @IsArray()
  @IsNumber({}, { each: true })
  acceptedUserIds: number[];

  @ApiProperty()
  @IsEnum(ERematchRequestStatus)
  status: ERematchRequestStatus;

  @ApiProperty()
  @Type(() => Date)
  @IsDate()
  createdAt: Date;

  @ApiProperty()
  @Type(() => Date)
  @IsDate()
  updatedAt: Date;

  @ApiProperty()
  @Type(() => Date)
  @IsDate()
  expiresAt: Date;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsUUID()
  startedLobbyId?: string;
}

// Response DTO for LobbyNewEntity
export class LobbyResponseDto extends PickType(LobbyEntity, [
  'id',
  'creatorId',
  'opponentId',
  'gameId',
  'stake',
  'originalStake',
  'minRating',
  'maxRating',
  'status',
  'createdAt',
  ...PICK_RETURN_TIME_FIELDS,
]) {
  // Relation fields
  @ApiProperty()
  @IsOptional()
  creator?: {
    id: number;
    name: string;
    avatar?: string;
  };

  @ApiProperty()
  @IsOptional()
  opponent?: {
    id: number;
    name: string;
    avatar?: string;
  };
}

export class LobbyStatisticDto {
  @ApiProperty()
  @IsNumber()
  activeLobbies: number;

  @ApiProperty()
  @IsNumber()
  activeMatches: number;
}
